import { useRecoilValue } from 'recoil';
import { toPointMm } from '~/domain/geometry/patientGeometry';
import {
    displayedCanvasPx,
    distanceFromDisplayedPlaneMm,
} from '~/engine/view/planeViewGeometry';
import { PlaneView } from '~/engine/view/views/PlaneView';
import type { CanvasFrameGeometry } from '~/engine/view/TextureView';
import {
    redrawEventsOver,
    usePlaneRedrawTick,
} from '~/hooks/viewer/annotationOverlay';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { petWindowMaxState } from '~/state/atoms/toolsAtoms';
import { OFF_PLANE_OPACITY } from './AnnotationOverlaySvg';
import {
    OVERLAY_LABEL_FILL,
    OVERLAY_LABEL_TEXT_STYLE,
} from './overlayLabelText';
import {
    POINT_MARKER_CROSS_ARM_PX,
    POINT_MARKER_FILL_ALPHA_OFF_PLANE,
    POINT_MARKER_FILL_ALPHA_ON_PLANE,
    POINT_MARKER_LABEL_FONT_SIZE_PX,
    POINT_MARKER_LABEL_OFFSET_X_PX,
    POINT_MARKER_LABEL_OFFSET_Y_PX,
    POINT_MARKER_OFF_SLICE_DASH,
    POINT_MARKER_RADIUS_PX,
    POINT_MARKER_STROKE_WIDTH_PX,
} from './pointMarkerGeometry';

/**
 * Maximum distance in mm from the current viewing plane at which the
 * marker is considered "on-plane": at least half the largest expected
 * slice spacing, so a maximum falling between two slices still reads
 * as on the nearest one (PET spacing runs 2–5 mm).
 */
const ON_PLANE_THRESHOLD_MM = 2.5;
/**
 * Magenta: the one hue no neighbouring layer owns. The hot-iron PET
 * colormap ramps black → red → yellow → white, the VOI box is the
 * theme's secondary orange, measurements are amber, the crosshair is
 * the primary teal, and POIs draw in their ROI's own color — magenta
 * collides with none of them on either the PET or the CT background.
 */
const MARKER_COLOR = '#e040fb';
/** The stroke color's RGB components, for the ring's alpha fill. */
const MARKER_RGB = '224, 64, 251';

interface MaxIntensityMarkerOverlayProps {
    view: PlaneView | null;
    frame?: CanvasFrameGeometry;
}

/**
 * Viewport marker for the SUV Threshold panel's SUVmax voxel — the
 * argmax of the RAW statistics window (the SUVmax the panel quotes),
 * NEVER the cleaned/applied contour: the cleanup stages (fill,
 * smoothing, min-size, keep-largest) can reshape the structure but
 * cannot move the measurement, and the marker exists to prove where
 * that measurement was taken — on the intended lesion, not on a hotter
 * structure (heart, brain, bladder) clipped by the box edge.
 *
 * Persistent in every plane view while the panel publishes a position
 * ({@link petWindowMaxState}); off-plane it DIMS (dashed,
 * reduced opacity) rather than disappears, so the orthogonal views
 * always show where the maximum sits. The glyph and its label take the
 * point marker's geometry, so every point-like annotation reads alike;
 * the color separates it — magenta marks a measurement, never an ROI's
 * own color. The label's color is not the marker's: see
 * {@link OVERLAY_LABEL_TEXT_STYLE} for why a value read over five
 * different colormaps is white on an outline.
 *
 * The text label beside the glyph quotes the SUVmax itself ("SUVmax
 * 7.4", SUVbw to 1 dp) — the SAME value the panel's stats row shows,
 * published together with the position in one atom, never recomputed
 * here. Unitless like the POI name labels: the g/ml unit is on the
 * panel row, and a bare SUVbw number is the clinical convention. The
 * label rides inside the marker's dim group, so off-plane it fades
 * with the glyph, and — like everything in this overlay — it never
 * intercepts pointer input.
 */
const MaxIntensityMarkerOverlay = ({
    view,
    frame,
}: MaxIntensityMarkerOverlayProps) => {
    const windowMax = useRecoilValue(petWindowMaxState);
    const viewManager = useRecoilValue(viewManagerState);
    // Re-project on navigation and zoom, unless the frame announces them —
    // the projection depends on view state React does not observe. The tick
    // itself is unused: the projection below is computed inline on every
    // render.
    usePlaneRedrawTick(viewManager, redrawEventsOver(frame));

    if (!view || !windowMax) {
        return null;
    }

    const { positionMm, suvValue } = windowMax;
    const point = toPointMm(positionMm);
    const isOnPlane =
        Math.abs(distanceFromDisplayedPlaneMm(point, view, frame)) <
        ON_PLANE_THRESHOLD_MM;
    const canvasCoords = displayedCanvasPx(point, view, frame);

    return (
        <svg
            data-testid="max-intensity-marker"
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 7,
            }}
        >
            <g opacity={isOnPlane ? 1 : OFF_PLANE_OPACITY}>
                <circle
                    data-testid="max-intensity-marker-ring"
                    cx={canvasCoords[0]}
                    cy={canvasCoords[1]}
                    r={POINT_MARKER_RADIUS_PX}
                    fill={`rgba(${MARKER_RGB}, ${isOnPlane ? POINT_MARKER_FILL_ALPHA_ON_PLANE : POINT_MARKER_FILL_ALPHA_OFF_PLANE})`}
                    stroke={MARKER_COLOR}
                    strokeWidth={POINT_MARKER_STROKE_WIDTH_PX}
                    strokeDasharray={
                        isOnPlane ? 'none' : POINT_MARKER_OFF_SLICE_DASH
                    }
                />
                {/* The point marker's cross */}
                <line
                    x1={canvasCoords[0] - POINT_MARKER_CROSS_ARM_PX}
                    y1={canvasCoords[1]}
                    x2={canvasCoords[0] + POINT_MARKER_CROSS_ARM_PX}
                    y2={canvasCoords[1]}
                    stroke={MARKER_COLOR}
                    strokeWidth={POINT_MARKER_STROKE_WIDTH_PX}
                />
                <line
                    x1={canvasCoords[0]}
                    y1={canvasCoords[1] - POINT_MARKER_CROSS_ARM_PX}
                    x2={canvasCoords[0]}
                    y2={canvasCoords[1] + POINT_MARKER_CROSS_ARM_PX}
                    stroke={MARKER_COLOR}
                    strokeWidth={POINT_MARKER_STROKE_WIDTH_PX}
                />
                {/* Value label, in the point marker's label place: inside the
                    dim group, so it fades with the glyph off-plane. */}
                <text
                    data-testid="max-intensity-marker-label"
                    x={canvasCoords[0] + POINT_MARKER_LABEL_OFFSET_X_PX}
                    y={canvasCoords[1] + POINT_MARKER_LABEL_OFFSET_Y_PX}
                    fill={OVERLAY_LABEL_FILL}
                    fontSize={POINT_MARKER_LABEL_FONT_SIZE_PX}
                    fontFamily="sans-serif"
                    style={OVERLAY_LABEL_TEXT_STYLE}
                >
                    {`SUVmax ${suvValue.toFixed(1)}`}
                </text>
            </g>
        </svg>
    );
};

export default MaxIntensityMarkerOverlay;
